import { useCallback, useRef } from 'react';

// ============================================================================
// USE NEW ORDER SOUND HOOK - YENİ SİPARİŞ SESİ (Web Audio API)
// ============================================================================

export interface UseNewOrderSoundReturn {
  /** Yeni sipariş sesini çal */
  playNewOrderSound: () => void;
  /** Yeni sipariş sesini durdur */
  stopNewOrderSound: () => void;
}

/**
 * Yeni sipariş geldiğinde uyarı sesi çalan hook
 * Dikkat çekici çift bip pattern'i - restoran sipariş uyarısı
 */
export const useNewOrderSound = (): UseNewOrderSoundReturn => {
  // Interval referansı
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  
  /**
   * Yeni sipariş sesini durdur
   */
  const stopNewOrderSound = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    
    // AudioContext'i kapat
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // Zaten kapalı olabilir
      }
      audioContextRef.current = null;
    }
  }, []);
  
  /**
   * Yeni sipariş sesini çal (loop)
   */
  const playNewOrderSound = useCallback(() => {
    // Önceki sesi durdur
    stopNewOrderSound();
    
    // Web Audio API ile uyarı sesi
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;
      
      /**
       * Dikkat çekici uyarı sesi - 3 kısa bip pattern'i
       * Yüksek-Düşük-Yüksek frekans ile aciliyet hissi
       */
      const playAlertPattern = () => {
        if (!audioContextRef.current) return;
        
        const ctx = audioContextRef.current;
        const now = ctx.currentTime;
        
        // Ses parametreleri
        const beeps = [
          { freq: 1200, start: 0, duration: 0.12 },      // Yüksek bip
          { freq: 800, start: 0.15, duration: 0.12 },    // Düşük bip
          { freq: 1400, start: 0.30, duration: 0.15 },   // Daha yüksek bip
        ];
        
        beeps.forEach(({ freq, start, duration }) => {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);
          
          oscillator.frequency.value = freq;
          oscillator.type = 'square'; // Keskin, dikkat çekici ses
          
          // Ses seviyesi
          gainNode.gain.setValueAtTime(0, now + start);
          gainNode.gain.linearRampToValueAtTime(0.25, now + start + 0.02); // Hızlı yükseliş
          gainNode.gain.setValueAtTime(0.25, now + start + duration - 0.02);
          gainNode.gain.linearRampToValueAtTime(0, now + start + duration); // Hızlı düşüş
          
          oscillator.start(now + start);
          oscillator.stop(now + start + duration);
        });
      };
      
      // İlk uyarı
      playAlertPattern();
      
      // Her 1.5 saniyede bir tekrar (daha sık, daha acil)
      intervalIdRef.current = setInterval(() => {
        playAlertPattern();
      }, 1500);
      
    } catch (error) {
      console.error('📢 [NewOrderSound] Ses çalınamadı:', error);
    }
  }, [stopNewOrderSound]);
  
  return {
    playNewOrderSound,
    stopNewOrderSound,
  };
};

export default useNewOrderSound;
